import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { apiError, formatZAR, shopApi, useShop } from "./ShopContext";

export const STATUS = {
  PLACED: ["Order placed", "bg-amber-100 text-amber-800"],
  PACKING: ["Being packed", "bg-blue-100 text-blue-800"],
  READY: ["Ready for collection", "bg-emerald-100 text-emerald-800"],
  COLLECTED: ["Collected", "bg-slate-200 text-slate-700"],
  CANCELLED: ["Cancelled", "bg-rose-100 text-rose-800"],
};
export const StatusBadge = ({ s }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS[s]?.[1]}`} data-testid="order-status-badge">{STATUS[s]?.[0] || s}</span>;
const when = (iso) => new Date(iso).toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const OrderCard = ({ o, onCancel }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`my-order-${o.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><p className="font-bold text-slate-900">{o.id}</p><p className="text-xs text-slate-500">{when(o.created_at)}</p></div>
      <StatusBadge s={o.status} />
    </div>
    <ul className="mt-3 space-y-1 text-sm text-slate-700">
      {o.items.map((i) => <li key={i.product_id} className="flex justify-between gap-2"><span className="truncate">{i.quantity} × {i.name}</span><span>{formatZAR(i.line_total)}</span></li>)}
    </ul>
    <div className="mt-3 flex items-center justify-between border-t pt-3">
      <span className="font-extrabold">{formatZAR(o.total)}</span>
      {["PLACED", "PACKING"].includes(o.status) && onCancel && (
        <button type="button" onClick={() => onCancel(o.id)} className="text-sm font-semibold text-rose-600" data-testid={`my-order-cancel-${o.id}`}>Cancel order</button>
      )}
    </div>
  </div>
);

export const ShopOrderDone = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const [o, setO] = useState(state?.order || null);
  useEffect(() => {
    if (!o) shopApi.get("/orders/mine").then(({ data }) => setO(data.find((x) => x.id === id) || null)).catch(() => {});
  }, [id, o]);
  return (
    <div className="mx-auto max-w-xl text-center" data-testid="shop-order-done">
      <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />
      <h1 className="mt-4 text-3xl font-extrabold text-slate-900">Thank you! Order {id} is placed.</h1>
      <p className="mt-2 text-slate-600">We'll pack it and let you know when it's ready. Bring your order number and pay when you collect.</p>
      {o && <div className="mt-6 text-left"><OrderCard o={o} /></div>}
      <Link to="/shop" className="mt-6 inline-block rounded-full bg-[#D96B27] px-6 py-3 font-bold text-white" data-testid="order-done-continue">Continue shopping</Link>
    </div>
  );
};

export default function ShopAccount() {
  const { customer, signOut } = useShop();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => shopApi.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => setOrders([])), []);
  useEffect(() => {
    if (customer === false) navigate("/shop/login", { replace: true });
    if (customer) load();
  }, [customer, navigate, load]);

  const cancel = async (id) => {
    if (!window.confirm(`Cancel order ${id}?`)) return;
    try { await shopApi.post(`/orders/${id}/cancel`); toast.success("Order cancelled"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!customer) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#D96B27]" /></div>;
  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]" data-testid="shop-account-page">
      <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-5">
        {customer.picture && <img src={customer.picture} alt="" className="mb-3 h-14 w-14 rounded-full" />}
        <p className="font-bold text-slate-900" data-testid="shop-account-name">{customer.name}</p>
        <p className="text-sm text-slate-500">{customer.email}</p>
        {customer.phone && <p className="text-sm text-slate-500">{customer.phone}</p>}
        <p className={`mt-3 rounded-lg p-2 text-xs ${customer.pos_customer_id ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-600"}`} data-testid="shop-account-link-status">
          {customer.pos_customer_id ? "Linked to your store account — your discount shows on prices." : "Have a store account? Ask us at the counter to link it and see your discount online."}
        </p>
        <button type="button" onClick={async () => { await signOut(); navigate("/shop"); }} className="mt-4 flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-rose-600" data-testid="shop-logout-btn">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>
      <section>
        <h1 className="mb-4 text-2xl font-extrabold text-slate-900">My orders</h1>
        {orders === null ? <Loader2 className="h-6 w-6 animate-spin text-[#D96B27]" /> : orders.length === 0 ? (
          <p className="text-slate-500" data-testid="my-orders-empty">No orders yet. <Link to="/shop/products" className="font-semibold text-[#D96B27]">Start shopping</Link></p>
        ) : <div className="space-y-3">{orders.map((o) => <OrderCard key={o.id} o={o} onCancel={cancel} />)}</div>}
      </section>
    </div>
  );
}
